import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, Form, Input, Button, Alert, Typography, List, Space } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, CheckOutlined, MinusOutlined } from '@ant-design/icons';
import { PageLayout } from '../../components/PageLayout/PageLayout';

const { Title, Text } = Typography;

const ResetPasswordPage = () => {

    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [statusCode, setStatusCode] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const validatePassword = (pass) => {
        const hasLowerCase = /[a-z]/.test(pass);
        const hasUpperCase = /[A-Z]/.test(pass);
        const hasDigit = /\d/.test(pass);
        const minLength = pass.length >= 8;

        return hasLowerCase && hasUpperCase && hasDigit && minLength;
    };

    const getPasswordRequirements = (pass) => {
        return {
            hasLowerCase: /[a-z]/.test(pass),
            hasUpperCase: /[A-Z]/.test(pass),
            hasDigit: /\d/.test(pass),
            minLength: pass.length >= 8
        };
    };

    const requirements = getPasswordRequirements(password);
    const isPasswordValid = validatePassword(password);
    const doPasswordsMatch = password === confirmPassword && confirmPassword !== '';
    const isFormValid = isPasswordValid && doPasswordsMatch;

    const resetPassword = (newPassword) => {
        if (token) {
            axios.post(`https://api.banyano.app/api/v1/auth/resetpassword?token=${token}`, {
                password: newPassword
            })
                .then(response => {
                    console.log('Verification response:', response.data);
                    console.log('Status code:', response.status);
                    setStatusCode(response.status);
                })
                .catch(error => {
                    console.error('Verification error:', error);
                    console.log('Error status code:', error.response?.status);
                    setStatusCode(error.response?.status || 500);
                });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!validatePassword(password)) {
            setErrorMessage('Password must contain at least one lowercase letter, one uppercase letter, one digit, and be at least 8 characters long.');
            return;
        }

        if (password !== confirmPassword) {
            setErrorMessage('Passwords do not match.');
            return;
        }

        resetPassword(password);
    };

    const requirementsList = [
        { key: 'hasLowerCase', text: 'One lowercase letter', valid: requirements.hasLowerCase },
        { key: 'hasUpperCase', text: 'One uppercase letter', valid: requirements.hasUpperCase },
        { key: 'hasDigit', text: 'One digit', valid: requirements.hasDigit },
        { key: 'minLength', text: 'At least 8 characters', valid: requirements.minLength }
    ];

    return (
        <PageLayout>
            <Card
                variant="outlined"
                style={{
                    textAlign: 'center',
                    border: '2px solid #7a7a7a',
                    
                }}
            >
                {statusCode === null && (
                    <>
                        <Title
                            level={1}
                            style={{
                                
                                marginBottom: 30
                            }}
                        >
                            Reset Your Password
                        </Title>

                        <div style={{
                            textAlign: 'left',
                            marginBottom: 30,
                            padding: 20,
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: 8,
                            border: '1px solid rgba(124, 58, 237, 0.3)'
                        }}>
                            <Text style={{ color: '#e0e0e0', fontWeight: 600, fontSize: 14 }}>
                                Password requirements:
                            </Text>
                            <List
                                dataSource={requirementsList}
                                renderItem={item => (
                                    <List.Item style={{
                                        border: 'none',
                                        padding: '4px 0',
                                        color: item.valid ? '#10b981' : '#9ca3af',
                                        transition: 'color 0.3s ease'
                                    }}>
                                        <Space>
                                            {item.valid ?
                                                <CheckOutlined style={{ color: '#10b981', fontWeight: 'bold' }} /> :
                                                <MinusOutlined style={{ color: '#9ca3af' }} />
                                            }
                                            <Text style={{ color: 'inherit', fontSize: 13 }}>{item.text}</Text>
                                        </Space>
                                    </List.Item>
                                )}
                                style={{ marginTop: 10 }}
                            />
                        </div>

                        <Form
                            layout="vertical"
                            onFinish={handleSubmit}
                            style={{ textAlign: 'left' }}
                        >
                            <Form.Item
                                label="New Password"
                                required
                            >
                                <Input.Password
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Confirm Password"
                                required
                            >
                                <Input.Password
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                                    size="large"
                                />
                            </Form.Item>

                            {errorMessage && (
                                <Alert
                                    type="error"
                                    description={errorMessage}
                                    showIcon
                                    style={{
                                        marginBottom: 15,
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        borderLeft: '4px solid #ef4444',
                                        borderRadius: 12,
                                        fontSize: 14
                                    }}
                                />
                            )}

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    disabled={!isFormValid}
                                    block
                                    size="large"
                                    style={{
                                        background: !isFormValid ? 'linear-gradient(135deg, #4c4c5e 0%, #3a3a4a 100%)' : undefined
                                    }}
                                >
                                    Change Password
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                )}

                {statusCode === 200 && (
                    <>
                        <CheckCircleOutlined style={{ fontSize: 64, color: '#10b981', marginBottom: 20 }} />
                        <Title
                            level={1}
                            style={{
                                
                                marginBottom: 30
                            }}
                        >
                            Password Reset Successful!
                        </Title>
                        <Alert
                            type="success"
                            description="Your password has been successfully reset. You can now log in with your new password."
                            style={{
                                marginBottom: 25,
                                textAlign: 'left',
                                background: 'rgba(16, 185, 129, 0.1)',
                                borderLeft: '4px solid #10b981',
                                borderRadius: 12
                            }}
                            showIcon={false}
                        />
                        <Alert
                            type="info"
                            description="You can now close this page."
                            style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                
                                borderRadius: 8,
                                fontSize: '0.95rem'
                            }}
                            showIcon={false}
                        />
                    </>
                )}

                {statusCode !== 200 && statusCode !== null && (
                    <>
                        <CloseCircleOutlined style={{ fontSize: 64, color: '#ef4444', marginBottom: 20 }} />
                        <Title
                            level={1}
                            style={{
                                
                                marginBottom: 30
                            }}
                        >
                            Oops!
                        </Title>
                        <Alert
                            type="error"
                            description="Something went wrong. The reset link may be expired or invalid. Please request a new password reset."
                            style={{
                                marginBottom: 25,
                                textAlign: 'left',
                                background: 'rgba(239, 68, 68, 0.1)',
                                borderLeft: '4px solid #ef4444',
                                borderRadius: 12
                            }}
                            showIcon
                        />
                        <Alert
                            type="info"
                            description="You can now close this page."
                            style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                
                                borderRadius: 8,
                                fontSize: '0.95rem'
                            }}
                            showIcon={false}
                        />
                    </>
                )}
            </Card>
        </PageLayout>
    );
}

export default ResetPasswordPage;